import React, { useState, useEffect } from 'react';
import "@/App.css";
import { LANDING_PAGES, SERVICES_FOR_LINKS, BOROUGHS_FOR_LINKS } from './data/landingPages';
import { LandingPage } from './pages/LandingPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { trackLead } from './lib/tracking';

// --- CONFIGURATION ---
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_URL = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('http', 'ws').replace('https', 'wss');

const LANDING_SLUGS = new Set(LANDING_PAGES.map(p => p.slug));

// --- MAIN APP COMPONENT ---

const App = () => {
    // Application State
    const [currentView, setCurrentView] = useState(() => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            if (params.get('reset_token')) return 'reset-password';
            // Check if pathname matches a landing page slug
            const slug = window.location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
            if (slug === 'reviews') return 'reviews';
            if (slug && LANDING_SLUGS.has(slug)) return `landing:${slug}`;
        }
        return 'homeowner';
    });
    const [leads, setLeads] = useState([]);
    const [loadingLeads, setLoadingLeads] = useState(false);
    const [adminToken, setAdminToken] = useState(() => localStorage.getItem('adminToken'));
    const [adminInfo, setAdminInfo] = useState(() => {
        const stored = localStorage.getItem('adminInfo');
        return stored ? JSON.parse(stored) : null;
    });
    const [userId] = useState(() => {
        // Get or create a simple user ID for homeowner sessions
        let id = localStorage.getItem('userId');
        if (!id) {
            id = 'user-' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('userId', id);
        }
        return id;
    });

    // Block search indexing of admin/auth views (thin content, no SEO value)
    useEffect(() => {
        const noindexViews = ['login', 'reset-password', 'pro'];
        const existing = document.head.querySelector('meta[name="robots"]');
        const prevContent = existing ? existing.getAttribute('content') : null;
        if (noindexViews.includes(currentView)) {
            if (existing) {
                existing.setAttribute('content', 'noindex, nofollow');
            } else {
                const m = document.createElement('meta');
                m.setAttribute('name', 'robots');
                m.setAttribute('content', 'noindex, nofollow');
                m.setAttribute('data-dynamic-robots', 'true');
                document.head.appendChild(m);
            }
        }
        return () => {
            const el = document.head.querySelector('meta[name="robots"]');
            if (el) {
                if (prevContent) {
                    el.setAttribute('content', prevContent);
                } else if (el.getAttribute('data-dynamic-robots') === 'true') {
                    el.remove();
                }
            }
        };
    }, [currentView]);

    // --- ADMIN LOGIN COMPONENT ---
    
    const AdminLogin = () => {
        const [credentials, setCredentials] = useState({
            username: '',
            password: ''
        });
        const [isLoggingIn, setIsLoggingIn] = useState(false);
        const [loginError, setLoginError] = useState(null);

        // Forgot password state
        const [showForgot, setShowForgot] = useState(false);
        const [forgotUsername, setForgotUsername] = useState('');
        const [forgotSubmitting, setForgotSubmitting] = useState(false);
        const [forgotMsg, setForgotMsg] = useState(null);

        const handleChange = (e) => {
            setCredentials({ ...credentials, [e.target.name]: e.target.value });
        };

        const submitForgotPassword = async (e) => {
            e.preventDefault();
            setForgotSubmitting(true);
            setForgotMsg(null);
            try {
                const res = await fetch(`${API_URL}/admin/forgot-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: forgotUsername }),
                });
                const data = await res.json();
                if (res.ok) {
                    setForgotMsg({ type: 'success', text: data.message || 'If that account exists, a reset email has been sent.' });
                } else {
                    setForgotMsg({ type: 'error', text: data.detail || 'Failed to submit request.' });
                }
            } catch (err) {
                setForgotMsg({ type: 'error', text: 'Network error. Please try again.' });
            } finally {
                setForgotSubmitting(false);
            }
        };

        const handleLogin = async (e) => {
            e.preventDefault();
            setIsLoggingIn(true);
            setLoginError(null);

            try {
                const response = await fetch(`${API_URL}/admin/login`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(credentials),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.detail || 'Login failed');
                }
                
                // Store token and admin info
                localStorage.setItem('adminToken', data.access_token);
                localStorage.setItem('adminInfo', JSON.stringify(data.admin));
                setAdminToken(data.access_token);
                setAdminInfo(data.admin);
                
                // Switch to pro view
                setCurrentView('pro');

            } catch (error) {
                console.error("Login error:", error);
                let errorMessage = 'Invalid username or password';
                
                // Handle rate limiting
                if (error.message && error.message.includes('locked')) {
                    errorMessage = error.message;
                } else if (error.message && error.message.includes('429')) {
                    errorMessage = 'Too many login attempts. Please try again later.';
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setLoginError(errorMessage);
            } finally {
                setIsLoggingIn(false);
            }
        };

        const handleBack = () => {
            setCurrentView('homeowner');
        };

        return (
            <div className="bg-white p-6 md:p-10 rounded-xl shadow-2xl max-w-md mx-auto border-t-8 border-blue-600">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h2>
                <p className="text-gray-600 mb-6">Access the Pro Dashboard</p>
                
                {loginError && (
                    <div className="p-4 mb-4 rounded-lg font-medium bg-red-100 text-red-700">
                        {loginError}
                    </div>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                            Username
                        </label>
                        <input 
                            type="text" 
                            id="username" 
                            name="username" 
                            value={credentials.username} 
                            onChange={handleChange} 
                            required 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                            placeholder="Enter your username"
                            autoFocus
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                            Password
                        </label>
                        <input 
                            type="password" 
                            id="password" 
                            name="password" 
                            value={credentials.password} 
                            onChange={handleChange} 
                            required 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                            placeholder="Enter your password"
                        />
                    </div>

                    <div className="flex space-x-3">
                        <button 
                            type="button"
                            onClick={handleBack}
                            className="flex-1 py-3 rounded-lg text-lg font-bold transition duration-300 bg-gray-300 hover:bg-gray-400 text-gray-700"
                        >
                            Back
                        </button>
                        <button 
                            type="submit" 
                            disabled={isLoggingIn}
                            className={`flex-1 py-3 rounded-lg text-lg font-bold transition duration-300 uppercase tracking-wide 
                                ${isLoggingIn
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                                }`}
                        >
                            {isLoggingIn ? 'Logging in...' : 'Login'}
                        </button>
                    </div>
                </form>

                <div className="mt-4 text-center">
                    <button
                        type="button"
                        onClick={() => { setShowForgot(true); setForgotMsg(null); setForgotUsername(credentials.username || ''); }}
                        data-testid="forgot-password-link"
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                        Forgot password?
                    </button>
                </div>

                {showForgot && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                        data-testid="forgot-password-modal"
                        onClick={(e) => { if (e.target === e.currentTarget) setShowForgot(false); }}
                    >
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
                            <button
                                type="button"
                                onClick={() => setShowForgot(false)}
                                className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-2xl leading-none"
                            >×</button>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">Forgot Password</h3>
                            <p className="text-sm text-gray-500 mb-4">Enter your admin username. If the account exists, we'll email a password-reset link to the configured admin email addresses.</p>

                            {forgotMsg && (
                                <div
                                    data-testid="forgot-password-result"
                                    className={`p-3 mb-3 rounded-lg text-sm ${forgotMsg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
                                >
                                    {forgotMsg.text}
                                </div>
                            )}

                            <form onSubmit={submitForgotPassword} className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Username</label>
                                    <input
                                        type="text"
                                        value={forgotUsername}
                                        onChange={(e) => setForgotUsername(e.target.value)}
                                        required
                                        autoFocus
                                        data-testid="forgot-username-input"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div className="flex gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowForgot(false)}
                                        className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={forgotSubmitting}
                                        data-testid="submit-forgot-password"
                                        className={`flex-1 py-2 rounded-lg font-semibold text-white ${forgotSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                                    >
                                        {forgotSubmitting ? 'Sending…' : 'Send reset link'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // --- RESET PASSWORD COMPONENT ---
    const ResetPassword = () => {
        const token = React.useMemo(() => {
            const params = new URLSearchParams(window.location.search);
            return params.get('reset_token') || '';
        }, []);
        const [form, setForm] = useState({ new_password: '', confirm_password: '' });
        const [submitting, setSubmitting] = useState(false);
        const [msg, setMsg] = useState(null);
        const [done, setDone] = useState(false);

        const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

        const submit = async (e) => {
            e.preventDefault();
            setMsg(null);
            if (!token) {
                setMsg({ type: 'error', text: 'Missing reset token. Please use the link from your email.' });
                return;
            }
            if (form.new_password !== form.confirm_password) {
                setMsg({ type: 'error', text: 'Passwords do not match.' });
                return;
            }
            setSubmitting(true);
            try {
                const res = await fetch(`${API_URL}/admin/reset-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, new_password: form.new_password }),
                });
                const data = await res.json();
                if (res.ok) {
                    setDone(true);
                    setMsg({ type: 'success', text: data.message || 'Password reset successful. You can now log in.' });
                } else {
                    let err = 'Failed to reset password.';
                    if (data.detail) {
                        if (Array.isArray(data.detail)) {
                            err = data.detail.map(d => d.msg.replace('Value error, ', '')).join(' | ');
                        } else {
                            err = data.detail;
                        }
                    }
                    setMsg({ type: 'error', text: err });
                }
            } catch (err) {
                setMsg({ type: 'error', text: 'Network error. Please try again.' });
            } finally {
                setSubmitting(false);
            }
        };

        const goToLogin = () => {
            // Clean the URL and go to login
            window.history.replaceState({}, document.title, window.location.pathname);
            setCurrentView('login');
        };

        return (
            <div className="bg-white p-6 md:p-10 rounded-xl shadow-2xl max-w-md mx-auto border-t-8 border-indigo-600" data-testid="reset-password-view">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
                <p className="text-gray-600 mb-4">Set a new password for your admin account.</p>

                {msg && (
                    <div
                        data-testid="reset-password-result"
                        className={`p-3 mb-3 rounded-lg text-sm ${msg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
                    >
                        {msg.text}
                    </div>
                )}

                {!done ? (
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">New password</label>
                            <input
                                type="password"
                                name="new_password"
                                value={form.new_password}
                                onChange={handleChange}
                                required
                                autoComplete="new-password"
                                data-testid="reset-new-password-input"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Confirm new password</label>
                            <input
                                type="password"
                                name="confirm_password"
                                value={form.confirm_password}
                                onChange={handleChange}
                                required
                                autoComplete="new-password"
                                data-testid="reset-confirm-password-input"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                        <p className="text-xs text-gray-500">Min 8 chars, upper + lower + number + special (@$!%*?&).</p>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="submit-reset-password"
                            className={`w-full py-3 rounded-lg font-semibold text-white uppercase tracking-wide ${submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                        >
                            {submitting ? 'Resetting…' : 'Reset Password'}
                        </button>
                    </form>
                ) : (
                    <button
                        type="button"
                        onClick={goToLogin}
                        data-testid="go-to-login-btn"
                        className="w-full py-3 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 uppercase tracking-wide"
                    >
                        Go to Login
                    </button>
                )}
            </div>
        );
    };

    // --- HOMEOWNER LEAD FORM COMPONENT ---
    
    const HomeownerLeadForm = () => {
        const [formData, setFormData] = useState({
            service: '',
            zipCode: '',
            description: '',
            contactName: '',
            contactPhone: '',
        });
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [submissionMessage, setSubmissionMessage] = useState(null);

        const handleChange = (e) => {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            setIsSubmitting(true);
            setSubmissionMessage(null);

            try {
                const response = await fetch(`${API_URL}/leads`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                });

                const data = await response.json();

                if (response.ok) {
                    // Success case
                    console.log('Lead submitted:', data);

                    // Fire conversion event with lead details
                    trackLead({
                        method: 'form_submit',
                        location: 'homeowner_form',
                        extra: {
                            lead_id: data.id,
                            service: formData.service,
                            zip_code: formData.zipCode,
                        }
                    });

                    setSubmissionMessage({ 
                        type: 'success', 
                        text: 'Thank you for contacting us! We will get back to you shortly.' 
                    });
                    
                    setFormData({
                        service: '',
                        zipCode: '',
                        description: '',
                        contactName: '',
                        contactPhone: '',
                    });
                } else {
                    // Error case - use already parsed data
                    console.error("Submission failed:", data);
                    
                    // Extract validation error messages
                    let errorMessage = 'Error submitting project. Please check your inputs.';
                    
                    if (data.detail && Array.isArray(data.detail)) {
                        // Pydantic validation errors
                        const errors = data.detail.map(err => {
                            const field = err.loc[err.loc.length - 1];
                            const msg = err.msg.replace('Value error, ', '');
                            return `${field}: ${msg}`;
                        });
                        errorMessage = errors.join(' | ');
                    } else if (data.detail && typeof data.detail === 'string') {
                        errorMessage = data.detail;
                    }
                    
                    throw new Error(errorMessage);
                }

            } catch (error) {
                console.error("Error submitting lead:", error);
                setSubmissionMessage({ 
                    type: 'error', 
                    text: error.message || 'Error submitting project. Please try again.' 
                });
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <div className="bg-white p-6 md:p-10 rounded-xl shadow-2xl max-w-2xl mx-auto border-t-8 border-indigo-600">
                <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Find a Local Exterminator Service Pro</h2>
                        <p className="text-gray-600">Describe your project and we'll connect you with top-rated professionals.</p>
                    </div>
                    <a 
                        href="tel:+16462099955"
                        onClick={() => trackLead({ method: 'phone_click', location: 'hero_call_now' })}
                        className="flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition duration-200 shadow-md whitespace-nowrap"
                    >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                        Call Now
                    </a>
                </div>
                
                {submissionMessage && (
                    <div className={`p-4 mb-4 rounded-lg font-medium ${submissionMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {submissionMessage.text}
                    </div>
                )}
                
                {/* Emergency Contact Banner */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center">
                            <span className="text-2xl mr-3">🚨</span>
                            <div>
                                <p className="font-bold text-gray-900">Emergency Pest Problem?</p>
                                <p className="text-sm text-gray-600">Call us directly for immediate assistance</p>
                            </div>
                        </div>
                        <a 
                            href="tel:+16462099955"
                            onClick={() => trackLead({ method: 'phone_click', location: 'emergency_banner' })}
                            className="flex items-center px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition duration-200 shadow-lg text-lg"
                        >
                            📞 (646) 209-9955
                        </a>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    
                    <div>
                        <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                            Pest control service required (e.g., Inspection, Rats & Mice, Bed bugs, Cockroaches, Ants, Fleas or Mites, Hornets & Wasps, Spiders, Flies, Bees, Bird Proofing, Mosquitoes, Termites, Wildlife or Animal Removal)
                        </label>
                        <input 
                            type="text" 
                            id="service" 
                            name="service" 
                            value={formData.service} 
                            onChange={handleChange} 
                            required 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                            placeholder="e.g., Bed bugs inspection"
                        />
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                                Your Zip Code
                            </label>
                            <input 
                                type="text" 
                                id="zipCode" 
                                name="zipCode" 
                                value={formData.zipCode} 
                                onChange={handleChange} 
                                required 
                                pattern="\d{5}"
                                title="A 5-digit zip code"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                placeholder="e.g., 90210"
                            />
                        </div>
                        <div>
                            <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                                Your Name
                            </label>
                            <input 
                                type="text" 
                                id="contactName" 
                                name="contactName" 
                                value={formData.contactName} 
                                onChange={handleChange} 
                                required 
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                placeholder="John Doe"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-1">
                            Contact Phone Number
                        </label>
                        <input 
                            type="tel" 
                            id="contactPhone" 
                            name="contactPhone" 
                            value={formData.contactPhone} 
                            onChange={handleChange} 
                            required 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                            placeholder="(555) 555-1234"
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Project Details
                        </label>
                        <textarea 
                            id="description" 
                            name="description" 
                            rows="4" 
                            value={formData.description} 
                            onChange={handleChange} 
                            required 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                            placeholder="Describe what you need done in detail (e.g., 'Need new shingles installed on a 1500 sq ft roof with a 4/12 pitch.')"
                        ></textarea>
                    </div>

                    <button 
                        type="submit" 
                        disabled={isSubmitting}
                        className={`w-full py-3 rounded-lg text-lg font-bold transition duration-300 uppercase tracking-wide 
                            ${isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-green-500 hover:bg-green-600 text-white shadow-md'
                            }`}
                    >
                        {isSubmitting ? 'Submitting...' : 'Connect With Local Exterminator Pros'}
                    </button>
                </form>

                {/* Internal links for SEO - Services + Service Areas + Reviews */}
                <section className="mt-10 pt-8 border-t border-gray-200">
                    <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
                        <h2 className="text-lg font-semibold text-gray-900">Popular Pest Control Services</h2>
                        <a
                            href="/reviews"
                            className="text-sm font-semibold text-amber-600 hover:text-amber-700 hover:underline"
                            data-testid="home-reviews-link"
                        >
                            ★ Read Customer Reviews
                        </a>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 mb-6">
                        {SERVICES_FOR_LINKS.map(l => (
                            <a key={l.slug} href={`/${l.slug}`} className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm">
                                → {l.label}
                            </a>
                        ))}
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">Service Areas — NYC & Surrounding Counties</h2>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {BOROUGHS_FOR_LINKS.map(l => (
                            <a key={l.slug} href={`/${l.slug}`} className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm">
                                → {l.label}
                            </a>
                        ))}
                    </div>
                </section>
            </div>
        );
    };

    // --- SERVICE PRO DASHBOARD COMPONENT ---

    const ProDashboard = () => {
        const leadStatuses = ['New', 'Viewed', 'Contacted', 'Assigned', 'Completed'];
        const [stats, setStats] = useState(null);
        const [statsLoading, setStatsLoading] = useState(false);
        const [sendingDigest, setSendingDigest] = useState(false);
        const [digestMsg, setDigestMsg] = useState(null);

        // Reviews management
        const [reviews, setReviews] = useState([]);
        const [reviewsLoading, setReviewsLoading] = useState(false);
        const [showReviews, setShowReviews] = useState(false);
        const [reviewForm, setReviewForm] = useState({ customer_name: '', location: '', service_type: '', rating: 5, review_text: '', is_published: true });
        const [reviewSubmitting, setReviewSubmitting] = useState(false);
        const [reviewMsg, setReviewMsg] = useState(null);

        const fetchReviews = async () => {
            setReviewsLoading(true);
            try {
                const res = await fetch(`${API_URL}/admin/reviews`, {
                    headers: { 'Authorization': `Bearer ${adminToken}` }
                });
                if (res.ok) setReviews(await res.json());
            } finally {
                setReviewsLoading(false);
            }
        };

        useEffect(() => {
            if (showReviews) fetchReviews();
        }, [showReviews]);

        const submitReview = async (e) => {
            e.preventDefault();
            setReviewSubmitting(true);
            setReviewMsg(null);
            try {
                const res = await fetch(`${API_URL}/admin/reviews`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${adminToken}`
                    },
                    body: JSON.stringify({ ...reviewForm, rating: parseInt(reviewForm.rating, 10) }),
                });
                const data = await res.json();
                if (res.ok) {
                    setReviewMsg({ type: 'success', text: 'Review added.' });
                    setReviewForm({ customer_name: '', location: '', service_type: '', rating: 5, review_text: '', is_published: true });
                    fetchReviews();
                } else {
                    let err = 'Failed to add review.';
                    if (data.detail) {
                        err = Array.isArray(data.detail)
                            ? data.detail.map(d => d.msg.replace('Value error, ', '')).join(' | ')
                            : data.detail;
                    }
                    setReviewMsg({ type: 'error', text: err });
                }
            } catch (e) {
                setReviewMsg({ type: 'error', text: 'Network error.' });
            } finally {
                setReviewSubmitting(false);
            }
        };

        const deleteReview = async (id) => {
            if (!window.confirm('Delete this review?')) return;
            const res = await fetch(`${API_URL}/admin/reviews/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (res.ok) fetchReviews();
        };

        const togglePublish = async (review) => {
            const res = await fetch(`${API_URL}/admin/reviews/${review.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`
                },
                body: JSON.stringify({ is_published: !review.is_published }),
            });
            if (res.ok) fetchReviews();
        };

        useEffect(() => {
            const fetchStats = async () => {
                setStatsLoading(true);
                try {
                    const res = await fetch(`${API_URL}/admin/leads/stats`, {
                        headers: { 'Authorization': `Bearer ${adminToken}` }
                    });
                    if (res.ok) {
                        setStats(await res.json());
                    }
                } catch (e) {
                    console.error('stats error', e);
                } finally {
                    setStatsLoading(false);
                }
            };
            fetchStats();
        }, [leads.length]);

        const handleSendDigest = async () => {
            setSendingDigest(true);
            setDigestMsg(null);
            try {
                const res = await fetch(`${API_URL}/admin/digest/send`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${adminToken}` }
                });
                const data = await res.json();
                if (res.ok) {
                    setDigestMsg({ type: 'success', text: 'Digest email queued — check your inboxes shortly.' });
                } else {
                    setDigestMsg({ type: 'error', text: data.detail || 'Failed to send digest.' });
                }
            } catch (e) {
                setDigestMsg({ type: 'error', text: 'Network error sending digest.' });
            } finally {
                setSendingDigest(false);
            }
        };

        const handleIndexNow = async () => {
            try {
                const res = await fetch(`${API_URL}/admin/indexnow/submit-all`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${adminToken}` }
                });
                const data = await res.json();
                if (res.ok) {
                    setDigestMsg({ type: 'success', text: `IndexNow: submitted ${data.url_count} URLs to Bing/Yandex/DuckDuckGo.` });
                } else {
                    setDigestMsg({ type: 'error', text: data.detail || 'IndexNow submission failed.' });
                }
            } catch (e) {
                setDigestMsg({ type: 'error', text: 'Network error on IndexNow submit.' });
            }
        };

        // Change Password state
        const [showPwdModal, setShowPwdModal] = useState(false);
        const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
        const [pwdSubmitting, setPwdSubmitting] = useState(false);
        const [pwdMsg, setPwdMsg] = useState(null);

        const handlePwdChange = (e) => {
            setPwdForm({ ...pwdForm, [e.target.name]: e.target.value });
        };

        const submitPwdChange = async (e) => {
            e.preventDefault();
            setPwdMsg(null);
            if (pwdForm.new_password !== pwdForm.confirm_password) {
                setPwdMsg({ type: 'error', text: 'New password and confirmation do not match.' });
                return;
            }
            setPwdSubmitting(true);
            try {
                const res = await fetch(`${API_URL}/admin/change-password`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${adminToken}`
                    },
                    body: JSON.stringify({
                        current_password: pwdForm.current_password,
                        new_password: pwdForm.new_password,
                    })
                });
                const data = await res.json();
                if (res.ok) {
                    setPwdMsg({ type: 'success', text: 'Password updated. Logging you out — please log in again with your new password.' });
                    setTimeout(() => {
                        handleLogout();
                    }, 1800);
                } else {
                    let err = 'Failed to change password.';
                    if (data.detail) {
                        if (Array.isArray(data.detail)) {
                            err = data.detail.map(d => d.msg.replace('Value error, ', '')).join(' | ');
                        } else {
                            err = data.detail;
                        }
                    }
                    setPwdMsg({ type: 'error', text: err });
                }
            } catch (e) {
                setPwdMsg({ type: 'error', text: 'Network error. Please try again.' });
            } finally {
                setPwdSubmitting(false);
            }
        };

        const updateLeadStatus = async (leadId, newStatus) => {
            try {
                const response = await fetch(`${API_URL}/leads/${leadId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${adminToken}`
                    },
                    body: JSON.stringify({ status: newStatus }),
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        handleLogout();
                        return;
                    }
                    throw new Error('Failed to update lead status');
                }

                const updatedLead = await response.json();
                console.log(`Updated lead ${leadId} status to ${newStatus}`, updatedLead);

            } catch (error) {
                console.error("Error updating status:", error);
            }
        };

        const handleRequestReview = async (lead) => {
            const suggested = lead.review_requested_email || '';
            const email = window.prompt(
                `Send a review request email to ${lead.contactName || 'the customer'}?\n\nEnter their email address:`,
                suggested
            );
            if (!email) return;
            try {
                const res = await fetch(`${API_URL}/admin/leads/${lead.id}/request-review`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${adminToken}`
                    },
                    body: JSON.stringify({ email: email.trim() }),
                });
                const data = await res.json();
                if (res.ok) {
                    window.alert(`✅ Review request email queued for ${email.trim()}`);
                } else {
                    let err = 'Failed to send review request.';
                    if (data.detail) {
                        err = Array.isArray(data.detail)
                            ? data.detail.map(d => d.msg.replace('Value error, ', '')).join(' | ')
                            : data.detail;
                    }
                    window.alert(`❌ ${err}`);
                }
            } catch (e) {
                window.alert('❌ Network error sending review request.');
            }
        };

        const getStatusColor = (status) => {
            switch (status) {
                case 'New': return 'bg-red-100 text-red-800';
                case 'Viewed': return 'bg-yellow-100 text-yellow-800';
                case 'Contacted': return 'bg-blue-100 text-blue-800';
                case 'Assigned': return 'bg-indigo-100 text-indigo-800';
                case 'Completed': return 'bg-green-100 text-green-800';
                default: return 'bg-gray-100 text-gray-800';
            }
        };

        return (
            <div className="bg-white p-6 md:p-10 rounded-xl shadow-2xl border-t-8 border-blue-600">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Service Professional Lead Dashboard</h2>
                        <p className="text-gray-600">Real-time feed of new project requests in your area.</p>
                    </div>
                    {adminInfo && (
                        <div className="text-right">
                            <p className="text-sm text-gray-600">Logged in as</p>
                            <p className="font-semibold text-blue-600">{adminInfo.username}</p>
                            <button
                                type="button"
                                onClick={() => { setShowPwdModal(true); setPwdMsg(null); setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); }}
                                data-testid="change-password-btn"
                                className="mt-1 text-xs text-indigo-600 hover:text-indigo-800 underline"
                            >
                                Change password
                            </button>
                        </div>
                    )}
                </div>

                {/* Change Password Modal */}
                {showPwdModal && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                        data-testid="change-password-modal"
                        onClick={(e) => { if (e.target === e.currentTarget) setShowPwdModal(false); }}
                    >
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
                            <button
                                type="button"
                                onClick={() => setShowPwdModal(false)}
                                className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-2xl leading-none"
                                data-testid="close-password-modal"
                            >×</button>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">Change Password</h3>
                            <p className="text-sm text-gray-500 mb-4">Use a strong password — min 8 chars, upper + lower + number + special (@$!%*?&).</p>

                            {pwdMsg && (
                                <div
                                    data-testid="password-change-result"
                                    className={`p-3 mb-3 rounded-lg text-sm ${pwdMsg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
                                >
                                    {pwdMsg.text}
                                </div>
                            )}

                            <form onSubmit={submitPwdChange} className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Current password</label>
                                    <input
                                        type="password"
                                        name="current_password"
                                        value={pwdForm.current_password}
                                        onChange={handlePwdChange}
                                        required
                                        autoComplete="current-password"
                                        data-testid="current-password-input"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">New password</label>
                                    <input
                                        type="password"
                                        name="new_password"
                                        value={pwdForm.new_password}
                                        onChange={handlePwdChange}
                                        required
                                        autoComplete="new-password"
                                        data-testid="new-password-input"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Confirm new password</label>
                                    <input
                                        type="password"
                                        name="confirm_password"
                                        value={pwdForm.confirm_password}
                                        onChange={handlePwdChange}
                                        required
                                        autoComplete="new-password"
                                        data-testid="confirm-password-input"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                </div>
                                <div className="flex gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowPwdModal(false)}
                                        className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={pwdSubmitting}
                                        data-testid="submit-password-change"
                                        className={`flex-1 py-2 rounded-lg font-semibold text-white ${pwdSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                                    >
                                        {pwdSubmitting ? 'Updating…' : 'Update Password'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {loadingLeads && <p className="text-center p-8 text-indigo-500">Loading Leads...</p>}

                {/* Lead Volume Widget */}
                <div className="mb-6 grid md:grid-cols-2 gap-4" data-testid="lead-stats-widget">
                    {['last24h', 'last7d'].map((window) => {
                        const s = stats ? stats[window] : null;
                        const label = window === 'last24h' ? 'Last 24 Hours' : 'Last 7 Days';
                        return (
                            <div key={window} className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-xl p-5 shadow-sm">
                                <div className="flex items-baseline justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-indigo-900 uppercase tracking-wider">{label}</h3>
                                    <span className="text-3xl font-extrabold text-indigo-700" data-testid={`stats-${window}-total`}>
                                        {statsLoading && !s ? '—' : (s ? s.total : 0)}
                                    </span>
                                </div>
                                {s && s.total > 0 ? (
                                    <div className="space-y-2 text-xs text-gray-700">
                                        <div>
                                            <span className="font-semibold text-gray-800">Status:</span>{' '}
                                            {Object.entries(s.byStatus).map(([k, v]) => (
                                                <span key={k} className="inline-block bg-white px-2 py-0.5 rounded-full mr-1 mb-1 border border-gray-200">
                                                    {k}: <strong>{v}</strong>
                                                </span>
                                            ))}
                                        </div>
                                        {s.topZips && s.topZips.length > 0 && (
                                            <div>
                                                <span className="font-semibold text-gray-800">Top Zips:</span>{' '}
                                                {s.topZips.slice(0, 3).map(([z, c]) => (
                                                    <span key={z} className="inline-block bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full mr-1 mb-1 font-medium">
                                                        {z} ({c})
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {s.topServices && s.topServices.length > 0 && (
                                            <div className="truncate">
                                                <span className="font-semibold text-gray-800">Top Services:</span>{' '}
                                                <span className="text-gray-600">
                                                    {s.topServices.slice(0, 2).map(([svc, c]) => `${svc} (${c})`).join(' · ')}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-500 italic">No leads in this window yet.</p>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Digest trigger */}
                <div className="mb-6 flex items-center justify-between flex-wrap gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-700">
                        <span className="font-semibold">Daily digest</span> sends automatically at 7 AM ET to your configured email recipients.
                    </div>
                    <button
                        type="button"
                        onClick={handleSendDigest}
                        disabled={sendingDigest}
                        data-testid="send-digest-btn"
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${sendingDigest ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'}`}
                    >
                        {sendingDigest ? 'Sending…' : 'Send digest now'}
                    </button>
                </div>
                <div className="mb-6 flex items-center justify-between flex-wrap gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-700">
                        <span className="font-semibold">SEO re-crawl</span> — push all URLs to Bing, Yandex, and DuckDuckGo via IndexNow. Auto-fires on every review change.
                    </div>
                    <button
                        type="button"
                        onClick={handleIndexNow}
                        data-testid="indexnow-btn"
                        className="px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 shadow-sm"
                    >
                        Submit all to IndexNow
                    </button>
                </div>
                {digestMsg && (
                    <div data-testid="digest-result" className={`mb-4 p-3 rounded-lg text-sm ${digestMsg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                        {digestMsg.text}
                    </div>
                )}

                {/* Reviews Manager */}
                <div className="mb-6 border border-amber-200 rounded-lg overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setShowReviews(v => !v)}
                        data-testid="toggle-reviews-manager"
                        className="w-full flex items-center justify-between p-4 bg-amber-50 hover:bg-amber-100 transition"
                    >
                        <div className="flex items-center gap-2">
                            <span className="text-amber-600 text-xl">★</span>
                            <span className="font-semibold text-gray-900">Customer Reviews Manager</span>
                            <span className="text-sm text-gray-500">({reviews.length} total)</span>
                        </div>
                        <span className="text-gray-500 text-sm">{showReviews ? '▲ Hide' : '▼ Show'}</span>
                    </button>

                    {showReviews && (
                        <div className="p-4 bg-white space-y-4" data-testid="reviews-manager">
                            {/* Add review form */}
                            <form onSubmit={submitReview} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                                <h4 className="font-semibold text-gray-800">Add a new review</h4>
                                {reviewMsg && (
                                    <div className={`p-2 rounded text-xs ${reviewMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                        {reviewMsg.text}
                                    </div>
                                )}
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <input type="text" required placeholder="Customer name (e.g., Maria G.)" value={reviewForm.customer_name} onChange={e => setReviewForm({...reviewForm, customer_name: e.target.value})} data-testid="review-name-input" className="px-3 py-2 border border-gray-300 rounded text-sm" />
                                    <input type="text" placeholder="Location (e.g., Queens, NY)" value={reviewForm.location} onChange={e => setReviewForm({...reviewForm, location: e.target.value})} data-testid="review-location-input" className="px-3 py-2 border border-gray-300 rounded text-sm" />
                                    <input type="text" placeholder="Service type (e.g., Bed Bug Treatment)" value={reviewForm.service_type} onChange={e => setReviewForm({...reviewForm, service_type: e.target.value})} data-testid="review-service-input" className="px-3 py-2 border border-gray-300 rounded text-sm" />
                                    <select value={reviewForm.rating} onChange={e => setReviewForm({...reviewForm, rating: e.target.value})} data-testid="review-rating-input" className="px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                                        {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} star{n===1?'':'s'}</option>)}
                                    </select>
                                </div>
                                <textarea required rows="3" placeholder="Review text (min 10 chars)" value={reviewForm.review_text} onChange={e => setReviewForm({...reviewForm, review_text: e.target.value})} data-testid="review-text-input" className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                                <div className="flex items-center justify-between gap-3">
                                    <label className="flex items-center gap-2 text-sm text-gray-700">
                                        <input type="checkbox" checked={reviewForm.is_published} onChange={e => setReviewForm({...reviewForm, is_published: e.target.checked})} data-testid="review-publish-input" />
                                        Publish immediately
                                    </label>
                                    <button type="submit" disabled={reviewSubmitting} data-testid="submit-review" className={`px-4 py-2 rounded font-semibold text-sm text-white ${reviewSubmitting ? 'bg-gray-400' : 'bg-amber-600 hover:bg-amber-700'}`}>
                                        {reviewSubmitting ? 'Saving…' : 'Add review'}
                                    </button>
                                </div>
                            </form>

                            {/* Review list */}
                            {reviewsLoading && <p className="text-center text-gray-500 text-sm py-4">Loading reviews…</p>}
                            {!reviewsLoading && reviews.length === 0 && <p className="text-center text-gray-500 text-sm py-4">No reviews yet.</p>}
                            <div className="space-y-2">
                                {reviews.map(r => (
                                    <div key={r.id} className={`border rounded-lg p-3 text-sm ${r.is_published ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-70'}`} data-testid={`admin-review-${r.id}`}>
                                        <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                                            <div>
                                                <span className="font-semibold text-gray-900">{r.customer_name}</span>
                                                {r.location && <span className="text-gray-500 ml-2">· {r.location}</span>}
                                                {r.service_type && <span className="text-gray-500 ml-2">· {r.service_type}</span>}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-amber-500">{'★'.repeat(r.rating)}<span className="text-gray-300">{'★'.repeat(5 - r.rating)}</span></span>
                                                <button type="button" onClick={() => togglePublish(r)} className={`text-xs px-2 py-1 rounded ${r.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`} data-testid={`toggle-publish-${r.id}`}>
                                                    {r.is_published ? 'Published' : 'Hidden'}
                                                </button>
                                                <button type="button" onClick={() => deleteReview(r.id)} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200" data-testid={`delete-review-${r.id}`}>
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-gray-700 italic">"{r.review_text}"</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                
                {!loadingLeads && leads.length === 0 && (
                    <p className="text-center p-8 text-gray-500 bg-gray-50 rounded-lg">
                        No new leads available at this time.
                    </p>
                )}

                <div className="space-y-6">
                    {leads.map(lead => (
                        <div key={lead.id} className="p-4 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition duration-200 bg-gray-50">
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="text-xl font-semibold text-indigo-700">
                                    {lead.service} ({lead.zipCode})
                                </h3>
                                <span className={`px-3 py-1 text-sm font-bold rounded-full ${getStatusColor(lead.status)}`}>
                                    {lead.status}
                                </span>
                            </div>

                            <p className="text-gray-700 text-sm mb-3 italic">
                                "{lead.description}"
                            </p>

                            <div className="grid grid-cols-2 text-sm gap-2 mb-4">
                                <p><span className="font-medium text-gray-800">Homeowner:</span> {lead.contactName}</p>
                                <p>
                                    <span className="font-medium text-gray-800">Phone:</span>{' '}
                                    <a href={`tel:${lead.contactPhone}`} className="text-blue-500 hover:underline">
                                        {lead.contactPhone}
                                    </a>
                                </p>
                            </div>
                            
                            {/* Status Update Control */}
                            <div className="flex items-center space-x-3 flex-wrap gap-2">
                                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                    Update Status:
                                </label>
                                <select 
                                    value={lead.status} 
                                    onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm bg-white focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {leadStatuses.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>

                                {lead.status === 'Completed' && (
                                    <button
                                        type="button"
                                        onClick={() => handleRequestReview(lead)}
                                        data-testid={`request-review-${lead.id}`}
                                        title={lead.review_requested_at ? `Already sent ${new Date(lead.review_requested_at).toLocaleDateString()} to ${lead.review_requested_email}` : 'Email customer a review request'}
                                        className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${lead.review_requested_at ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-amber-500 text-white hover:bg-amber-600 shadow'}`}
                                    >
                                        {lead.review_requested_at ? '★ Review Requested' : '★ Request Review'}
                                    </button>
                                )}
                            </div>

                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Handle logout
    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminInfo');
        setAdminToken(null);
        setAdminInfo(null);
        setCurrentView('homeowner');
        setLeads([]);
    };

    // Handle switching to Pro view
    const handleSwitchToProView = () => {
        if (adminToken) {
            setCurrentView('pro');
        } else {
            setCurrentView('login');
        }
    };

    // Fetch leads and set up WebSocket when admin is logged in and on pro view
    useEffect(() => {
        if (currentView !== 'pro' || !adminToken) {
            return;
        }

        // Initial fetch of all leads
        const fetchLeads = async () => {
            setLoadingLeads(true);
            try {
                const response = await fetch(`${API_URL}/leads`, {
                    headers: {
                        'Authorization': `Bearer ${adminToken}`
                    }
                });
                
                if (!response.ok) {
                    if (response.status === 401) {
                        handleLogout();
                        return;
                    }
                    throw new Error('Failed to fetch leads');
                }
                
                const data = await response.json();
                setLeads(data);
            } catch (error) {
                console.error("Error fetching leads:", error);
            } finally {
                setLoadingLeads(false);
            }
        };

        fetchLeads();

        // Set up WebSocket for real-time updates
        const ws = new WebSocket(`${WS_URL}/api/ws/leads`);

        ws.onopen = () => {
            console.log('WebSocket connected, sending authentication...');
            // Send authentication token as first message
            ws.send(JSON.stringify({ token: adminToken }));
        };

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                console.log('WebSocket message received:', message);

                if (message.type === 'authenticated') {
                    console.log('✅ WebSocket authenticated successfully');
                } else if (message.type === 'error') {
                    console.error('WebSocket error:', message.message);
                    handleLogout();
                } else if (message.type === 'new_lead') {
                    // Add new lead to the top of the list
                    setLeads(prevLeads => [message.data, ...prevLeads]);
                } else if (message.type === 'lead_updated') {
                    // Update existing lead
                    setLeads(prevLeads => 
                        prevLeads.map(lead => 
                            lead.id === message.data.id ? message.data : lead
                        )
                    );
                }
            } catch (error) {
                console.error('Error processing WebSocket message:', error);
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        ws.onclose = () => {
            console.log('WebSocket disconnected');
        };

        // Cleanup on unmount
        return () => {
            ws.close();
        };
    }, [currentView, adminToken]);

    // --- RENDER MAIN LAYOUT ---

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8" itemScope itemType="https://schema.org/WebPage">
            <header className="max-w-4xl mx-auto mb-6" role="banner">
                <div className="flex justify-between items-center flex-wrap gap-4">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight" itemProp="name">
                            <span className="text-indigo-600">Exterminator Pro</span>Service Connect
                        </h1>
                        <p className="text-sm text-gray-600 mt-1">NYC Pest Control · Queens, Brooklyn, Manhattan, Bronx, Staten Island</p>
                    </div>
                    
                    <div className="flex space-x-3">
                        <button 
                            onClick={() => { window.history.pushState({}, '', '/'); setCurrentView('homeowner'); }}
                            className={`px-4 py-2 rounded-full font-semibold transition duration-200 
                                ${currentView === 'homeowner' 
                                    ? 'bg-indigo-600 text-white shadow-md' 
                                    : 'bg-white text-indigo-600 border border-indigo-600 hover:bg-indigo-50'
                                }`}
                        >
                            Homeowner View
                        </button>
                        
                        {adminToken ? (
                            <>
                                <button 
                                    onClick={() => setCurrentView('pro')}
                                    className={`px-4 py-2 rounded-full font-semibold transition duration-200 
                                        ${currentView === 'pro' 
                                            ? 'bg-blue-600 text-white shadow-md' 
                                            : 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-50'
                                        }`}
                                >
                                    Pro Dashboard
                                </button>
                                <button 
                                    onClick={handleLogout}
                                    className="px-4 py-2 rounded-full font-semibold transition duration-200 bg-red-500 text-white hover:bg-red-600"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <button 
                                onClick={handleSwitchToProView}
                                className={`px-4 py-2 rounded-full font-semibold transition duration-200 
                                    ${currentView === 'login' 
                                        ? 'bg-blue-600 text-white shadow-md' 
                                        : 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-50'
                                    }`}
                            >
                                Pro Login
                            </button>
                        )}
                    </div>
                </div>
                
                {adminToken && adminInfo && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-blue-700 border border-blue-200">
                        <span className="font-semibold">Admin:</span> {adminInfo.username}
                        {adminInfo.full_name && <span> ({adminInfo.full_name})</span>}
                    </div>
                )}
            </header>

            <main className="max-w-4xl mx-auto" role="main" itemProp="mainContentOfPage">
                {currentView === 'homeowner' && <HomeownerLeadForm />}
                {currentView === 'login' && <AdminLogin />}
                {currentView === 'reset-password' && <ResetPassword />}
                {currentView === 'reviews' && (
                    <ReviewsPage
                        onRequestService={() => {
                            window.history.pushState({}, '', '/');
                            setCurrentView('homeowner');
                        }}
                    />
                )}
                {currentView === 'pro' && adminToken && <ProDashboard />}
                {currentView.startsWith('landing:') && (
                    <LandingPage
                        slug={currentView.split(':')[1]}
                        onRequestService={() => {
                            window.history.pushState({}, '', '/');
                            setCurrentView('homeowner');
                        }}
                    />
                )}
            </main>
            
            {/* Hidden SEO content for voice search and AI crawlers */}
            <div className="sr-only" aria-hidden="true">
                {/* NYC and NY State Coverage */}
                <h2>New York City Exterminator Services - All 5 Boroughs</h2>
                <p>Professional NYC exterminator serving all five boroughs: Manhattan exterminator, Brooklyn pest control, Queens exterminator, Bronx pest control, Staten Island exterminator. Licensed and insured pest control for all NYC zip codes including 10001, 10002, 10003, 11201, 11211, 11215, 11101, 11354, 10451, 10301 and over 200+ NYC zip codes.</p>
                
                <h2>Manhattan Exterminator - All Neighborhoods</h2>
                <p>Expert pest control Manhattan: Upper East Side exterminator, Upper West Side pest control, Midtown Manhattan exterminator, Downtown Manhattan pest control, Harlem exterminator, Washington Heights pest control, Chelsea exterminator, Greenwich Village pest control, SoHo exterminator, Tribeca pest control, Financial District exterminator. Serving zip codes 10001-10282.</p>
                
                <h2>Brooklyn Exterminator - All Neighborhoods</h2>
                <p>Professional Brooklyn pest control: Williamsburg exterminator, Park Slope pest control, Brooklyn Heights exterminator, DUMBO pest control, Bushwick exterminator, Bedford-Stuyvesant pest control, Crown Heights exterminator, Flatbush pest control, Bay Ridge exterminator, Sunset Park pest control, Coney Island exterminator. Covering zip codes 11201-11256.</p>
                
                <h2>Queens Exterminator - All Neighborhoods</h2>
                <p>Licensed Queens pest control: Astoria exterminator, Long Island City pest control, Flushing exterminator, Jamaica pest control, Forest Hills exterminator, Elmhurst pest control, Jackson Heights exterminator, Rego Park pest control, Bayside exterminator, Woodside pest control. Serving zip codes 11101-11697.</p>
                
                <h2>Bronx Exterminator - All Neighborhoods</h2>
                <p>Certified Bronx pest control: Riverdale exterminator, Fordham pest control, Parkchester exterminator, Pelham Bay pest control, Throggs Neck exterminator, Morris Park pest control, Soundview exterminator, Hunts Point pest control. Covering zip codes 10451-10475.</p>
                
                <h2>Staten Island Exterminator - All Areas</h2>
                <p>Professional Staten Island pest control: St. George exterminator, Tompkinsville pest control, Stapleton exterminator, Port Richmond pest control, New Brighton exterminator, West Brighton pest control, Tottenville exterminator. Serving zip codes 10301-10314.</p>
                
                <h2>Western New York Pest Control - Buffalo and Beyond</h2>
                <p>Buffalo exterminator, Cheektowaga pest control, West Seneca exterminator, Amherst pest control, Tonawanda exterminator, Niagara Falls pest control. Serving Erie County zip codes 14201-14228 and all Western NY.</p>
                
                <h2>Rochester Exterminator - Monroe County Pest Control</h2>
                <p>Rochester pest control, Irondequoit exterminator, Greece pest control, Brighton exterminator, Penfield pest control, Webster exterminator. Covering Rochester zip codes 14604-14627 and Finger Lakes region.</p>
                
                <h2>Syracuse Exterminator - Central New York Pest Control</h2>
                <p>Syracuse pest control, Clay exterminator, Salina pest control, Camillus exterminator, Liverpool pest control. Serving Onondaga County zip codes 13201-13235 and Central NY.</p>
                
                <h2>Albany Exterminator - Capital District Pest Control</h2>
                <p>Albany pest control, Cohoes exterminator, Watervliet pest control, Troy exterminator, Schenectady pest control. Covering Albany County zip codes 12201-12212 and Capital District.</p>
                
                <h2>Westchester County Exterminator Services</h2>
                <p>Yonkers exterminator, New Rochelle pest control, Mount Vernon exterminator, White Plains pest control, Scarsdale exterminator, Rye pest control. Serving all Westchester zip codes 10701-10710 and beyond.</p>
                
                <h2>Long Island Exterminator - Nassau and Suffolk Counties</h2>
                <p>Nassau County exterminator: Hempstead pest control, Long Beach exterminator, Glen Cove pest control. Suffolk County exterminator: Huntington pest control, Islip exterminator, Brookhaven pest control, Babylon exterminator. Serving all Long Island zip codes.</p>
                
                <h2>Hudson Valley Pest Control Services</h2>
                <p>Orange County exterminator: Newburgh pest control, Middletown exterminator, Port Jervis pest control. Dutchess County exterminator: Poughkeepsie pest control, Beacon exterminator, Fishkill pest control. Rockland County exterminator: Spring Valley pest control, Nyack exterminator, Suffern pest control.</p>
                
                <h2>All New York State Zip Codes Covered</h2>
                <p>Professional exterminator services available in over 2,000 New York State zip codes. From NYC to Buffalo, Rochester to Albany, Syracuse to Yonkers - we connect you with certified, licensed, and insured pest control professionals throughout New York State. 24/7 emergency service available.</p>
                
                <h2>NYC Bed Bug Treatment - All Boroughs</h2>
                <p>Bed bug exterminator NYC, bed bug treatment Manhattan, bed bug removal Brooklyn, bed bug inspection Queens, bed bug control Bronx, bed bug extermination Staten Island. Fast, effective, guaranteed bed bug removal for all NYC neighborhoods and zip codes.</p>
                
                <h2>NYC Rat and Mice Control</h2>
                <p>Rat exterminator NYC, mice removal Manhattan, rodent control Brooklyn, rat trapping Queens, mouse extermination Bronx, rodent proofing Staten Island. Professional rat and mice control for commercial and residential properties throughout New York City.</p>
                
                <h2>NYC Cockroach Exterminator</h2>
                <p>Cockroach exterminator NYC, roach removal Manhattan, cockroach control Brooklyn, roach treatment Queens, cockroach elimination Bronx, roach extermination Staten Island. German cockroach, American cockroach, and all species removal.</p>
                
                <h2>Emergency Pest Control NYC - 24/7 Service</h2>
                <p>24 hour exterminator NYC, emergency pest control Manhattan, same day exterminator Brooklyn, urgent pest removal Queens, emergency extermination Bronx, 24/7 pest control Staten Island. Immediate response for urgent pest problems throughout New York City and State.</p>
                
                <h2>Commercial Pest Control New York</h2>
                <p>Commercial exterminator NYC, restaurant pest control Manhattan, office building exterminator Brooklyn, retail pest control Queens, warehouse extermination Bronx, commercial property pest control Staten Island. Licensed commercial pest management for all NY businesses.</p>
                
                <h2>How to find a local exterminator near me in New York</h2>
                <p>Looking for a professional exterminator near you in New York? Exterminator ProService Connect helps you find certified pest control professionals in your area. Simply enter your NY zip code and describe your pest problem to get connected with local exterminators who can help with bed bugs, rats, mice, cockroaches, ants, termites, and more. Serving all 2,000+ New York State zip codes.</p>
                
                <h2>What pest control services do we offer in New York</h2>
                <p>We connect you with New York exterminators who provide comprehensive pest control services including bed bug treatment, rodent removal, termite inspection, cockroach extermination, ant control, flea and mite treatment, wasp and hornet removal, spider control, mosquito prevention, and wildlife removal services. Available throughout NYC, Long Island, Westchester, Hudson Valley, Buffalo, Rochester, Syracuse, Albany and all NY regions.</p>
                
                <h2>Emergency pest control services available throughout New York</h2>
                <p>Need urgent pest control help in New York? Our network of professional exterminators offers 24/7 emergency services for immediate pest problems. Get fast response times and same-day service when you need it most. Available in all five NYC boroughs and throughout New York State.</p>
                
                <h2>Licensed and certified New York exterminators</h2>
                <p>All exterminators in our network are New York State licensed, certified, and insured professionals with years of experience in pest control and extermination services. Fully compliant with NY DEC regulations and NYC Department of Health requirements.</p>
            </div>
        </div>
    );
};

export default App;
